import { Plus, X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Checkbox, Input } from '@/components/ui';
import { PAYMENT_METHODS, type PaymentTerms } from '@/features/settings/business-api';
import {
  addStage,
  balanceSchedule,
  canAddStage,
  removeStage,
  scheduleIssue,
  setStagePercent,
  stageIssue,
  type ScheduleIssue,
} from './payment-terms';

/**
 * לוח תשלומים: שלבים עם אחוזים, ואמצעי תשלום. משמש גם בהגדרות (ברירת
 * המחדל) וגם בהצעה עצמה.
 *
 * השלב האחרון הוא היתרה: הוא מחושב אוטומטית (100 פחות השאר), כך שהסכום
 * תמיד 100%. אם השלבים האחרים עוברים 100% — שגיאה ליד השדה שנערך.
 */
export function PaymentTermsEditor({
  value,
  onChange,
  totalLabel,
}: {
  value: PaymentTerms;
  onChange: (v: PaymentTerms) => void;
  /** סכום ההצעה, לתצוגת הסכום לכל שלב (בשקלים). */
  totalLabel?: (percent: number) => string;
}) {
  const { t } = useTranslation(['quotes', 'common']);
  const schedule = value.schedule;
  const setSchedule = (s: PaymentTerms['schedule']) => onChange({ ...value, schedule: s });
  const setLabel = (i: number, label: string) =>
    setSchedule(schedule.map((r, j) => (j === i ? { ...r, label } : r)));
  const issue = scheduleIssue(schedule);
  const single = schedule.length === 1;
  // השדה שנערך אחרון — שם מוצגת השגיאה "עוברים 100%" (ולא על כל השלבים).
  const [edited, setEdited] = useState<number | null>(null);
  const exceedsRow = edited !== null && edited < schedule.length - 1 ? edited : 0;

  const presets: Array<{ label: string; schedule: PaymentTerms['schedule'] }> = [
    { label: t('terms.presetOnCompletion'), schedule: [{ label: t('terms.onCompletion'), percent: 100 }] },
    {
      label: '30/70',
      schedule: [
        { label: t('terms.deposit'), percent: 30 },
        { label: t('terms.onCompletion'), percent: 70 },
      ],
    },
    {
      label: '30/40/30',
      schedule: [
        { label: t('terms.deposit'), percent: 30 },
        { label: t('terms.onStart'), percent: 40 },
        { label: t('terms.onCompletion'), percent: 30 },
      ],
    },
  ];

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-fg-muted">{t('terms.quickPreset')}</span>
        {presets.map((p) => (
          <Button key={p.label} type="button" size="sm" variant="secondary" onClick={() => setSchedule(balanceSchedule(p.schedule))}>
            {p.label}
          </Button>
        ))}
      </div>
      <ul className="space-y-2">
        {schedule.map((row, i) => {
          const last = i === schedule.length - 1;
          const raw = stageIssue(schedule, i);
          const rowIssue = raw === 'exceeds' && i !== exceedsRow ? null : raw;
          const errorId = `terms-stage-${i}-error`;
          const hintId = `terms-stage-${i}-hint`;
          return (
            <li key={i} className="space-y-1">
              <div className="flex items-center gap-2">
                <Input
                  className="min-w-0 flex-1"
                  aria-label={t('terms.stageAria')}
                  value={row.label}
                  maxLength={80}
                  invalid={rowIssue === 'noLabel'}
                  onChange={(e) => setLabel(i, e.target.value)}
                />
                <div className="relative w-24 shrink-0">
                  <Input
                    type="number"
                    dir="ltr"
                    inputMode="numeric"
                    min={1}
                    max={100}
                    aria-label={t('terms.percentAria')}
                    aria-describedby={rowIssue && rowIssue !== 'noLabel' ? errorId : last ? hintId : undefined}
                    invalid={rowIssue === 'exceeds' || rowIssue === 'zero'}
                    // השלב האחרון הוא היתרה — מחושב, לא מוקלד.
                    readOnly={last}
                    tabIndex={last ? -1 : undefined}
                    className={last ? 'pe-7 bg-surface-sunken text-fg-muted' : 'pe-7'}
                    value={row.percent}
                    onChange={(e) => {
                      setEdited(i);
                      setSchedule(setStagePercent(schedule, i, Number(e.target.value)));
                    }}
                  />
                  <span className="pointer-events-none absolute inset-y-0 end-3 grid place-items-center text-xs text-fg-subtle">%</span>
                </div>
                {totalLabel ? <span className="w-24 shrink-0 text-end text-xs tabular text-fg-muted">{totalLabel(row.percent)}</span> : null}
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  aria-label={t('terms.removeStage')}
                  disabled={single}
                  onClick={() => {
                    setEdited(null);
                    setSchedule(removeStage(schedule, i));
                  }}
                >
                  <X aria-hidden />
                </Button>
              </div>
              {rowIssue ? (
                <p id={errorId} className="text-xs font-medium text-danger" role="alert">
                  {t(`terms.issue.${rowIssue}`)}
                </p>
              ) : last ? (
                <p id={hintId} className="text-2xs text-fg-subtle">
                  {single ? t('terms.singleHint') : t('terms.balanceHint')}
                </p>
              ) : null}
            </li>
          );
        })}
      </ul>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Button
          type="button"
          size="sm"
          variant="secondary"
          disabled={!canAddStage(schedule)}
          onClick={() => setSchedule(addStage(schedule, t('terms.extraPayment')))}
        >
          <Plus aria-hidden />
          {t('terms.addStage')}
        </Button>
        {issue && issue !== 'exceeds' && issue !== 'zero' && issue !== 'noLabel' ? (
          // שגיאות של שלב מסוים כבר מוצגות ליד השדה; כאן — מה שאינו של שלב אחד.
          <span className="text-xs font-medium text-danger" role="status">
            {t(`terms.issue.${issue}`)}
          </span>
        ) : (
          <span className="text-xs text-fg-muted" role="status">
            {t('terms.total', { sum: schedule.reduce((s, r) => s + r.percent, 0) })}
          </span>
        )}
      </div>
      <fieldset>
        <legend className="mb-1.5 text-xs text-fg-muted">{t('terms.methods')}</legend>
        <div className="flex flex-wrap gap-x-4 gap-y-2">
          {PAYMENT_METHODS.map((m) => (
            <label key={m} className="flex items-center gap-1.5 text-sm text-fg">
              <Checkbox
                checked={value.methods.includes(m)}
                onCheckedChange={(on) =>
                  onChange({ ...value, methods: on ? [...value.methods, m] : value.methods.filter((x) => x !== m) })
                }
              />
              {m}
            </label>
          ))}
        </div>
      </fieldset>
    </div>
  );
}

/** הסיבה שלוח התשלומים חוסם שמירה/שליחה — לסיכום ליד הכפתור. */
export function useScheduleIssueText(): (issue: ScheduleIssue) => string {
  const { t } = useTranslation('quotes');
  return (issue) => t('terms.blocking', { reason: t(`terms.issue.${issue}`) });
}
